import { Component, Input, Output, EventEmitter } from '@angular/core';
import { IAdminPanel, AdminPanelStatus } from '../admin-panel.interface';

/** One sub-tab in the consolidated Ticketing admin panel. */
interface TicketingTab {
  id: string;
  label: string;
  icon: string;
}

/**
 * Consolidated "Ticketing" administration panel.
 *
 * Replaces six separate left-nav items (Ticket Teams, SLA Policies, Assignment Rules, Skills,
 * Automation Rules, Macros) with a single nav item whose right panel has a Settings-style tab row
 * hosting the existing panel components unchanged. Each child takes [organizationId]; this wrapper
 * just passes the org down and shows one tab at a time.
 *
 * Pure UI consolidation — no schema/metadata: the tab list is a literal array pointing at the
 * existing components (unlike Settings, whose tabs are DB-driven SettingCategory records). The
 * section is plan-gated (Pro+) in the admin shell, same as the Tickets nav tab.
 */
@Component({
  standalone: false,
  selector: 'izzy-ticketing-admin',
  templateUrl: './ticketing-admin.component.html',
  styleUrls: ['./ticketing-admin.component.css'],
})
export class TicketingAdminComponent implements IAdminPanel {
  @Input() organizationId: string | undefined;
  /** Satisfies IAdminPanel so the admin shell's (statusChange) binding is valid; ticketing has no
   *  aggregate health to report, so this stays quiet. */
  @Output() statusChange = new EventEmitter<AdminPanelStatus>();

  protected readonly tabs: TicketingTab[] = [
    { id: 'ticket-teams', label: 'Ticket Teams', icon: 'fa-users-rectangle' },
    { id: 'sla-policies', label: 'SLA Policies', icon: 'fa-stopwatch' },
    { id: 'ticket-numbering', label: 'Ticket Numbering', icon: 'fa-hashtag' },
    { id: 'assignment-rules', label: 'Assignment Rules', icon: 'fa-route' },
    { id: 'skills', label: 'Skills', icon: 'fa-user-graduate' },
    { id: 'automation-rules', label: 'Automation Rules', icon: 'fa-wand-magic-sparkles' },
    { id: 'macros', label: 'Macros', icon: 'fa-bolt-lightning' },
  ];

  protected selectedTabId = this.tabs[0].id;

  selectTab(id: string): void {
    this.selectedTabId = id;
  }
}
